<template>
  <span
    ref="rootRef"
    class="badge text-static-white text-2xs from-text-negative-primary to-text-negative-accent absolute -top-2 block h-[17px] w-[35px] overflow-hidden rounded-full bg-linear-to-b px-1 py-0.5 leading-none font-medium"
  >
    new
    <span class="shine" aria-hidden="true" />
  </span>
</template>

<script setup lang="ts">
  import { ref, watch } from 'vue';

  const props = defineProps<{ pulseKey?: number }>();

  const rootRef = ref<HTMLElement | null>(null);

  // Micro «pop» of the badge in sync with the shared trigger (pulseKey from AppSwitchButton)
  watch(
    () => props.pulseKey,
    () => {
      const el = rootRef.value;
      if (!el) return;
      el.classList.remove('is-playing');
      void el.getBoundingClientRect();
      el.classList.add('is-playing');
    },
  );
</script>

<style scoped>
  .badge {
    transform-origin: center;
  }
  .badge.is-playing {
    /* hotel animation first; the badge «pops» on the tail of its settle — lively but smooth */
    animation: badge-pop 520ms cubic-bezier(0.34, 1.12, 0.64, 1) 560ms both;
  }
  @keyframes badge-pop {
    0% {
      transform: scale(1) rotate(0deg);
    }
    40% {
      transform: scale(1.2) rotate(-3deg);
    }
    70% {
      transform: scale(0.99) rotate(1deg);
    }
    100% {
      transform: scale(1) rotate(0deg);
    }
  }
  /* White «gloss» highlight: a diagonal stripe, clipped by the pill shape (overflow-hidden) */
  .shine {
    position: absolute;
    top: 0;
    bottom: 0;
    left: -70%;
    width: 70%;
    pointer-events: none;
    opacity: 0;
    background: linear-gradient(
      115deg,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 100%
    );
  }
  .badge.is-playing .shine {
    /* soft, unhurried sweep while the badge scales up */
    animation: badge-shine 780ms cubic-bezier(0.4, 0, 0.2, 1) 560ms both;
  }
  @keyframes badge-shine {
    0% {
      transform: translateX(0);
      opacity: 0;
    }
    35% {
      opacity: 1;
    }
    65% {
      opacity: 1;
    }
    100% {
      transform: translateX(300%);
      opacity: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .badge.is-playing,
    .badge.is-playing .shine {
      animation: none !important;
    }
  }
</style>
